import React, { useEffect, useMemo, useRef, useState } from "react";
// Si ya tienes API_BASE en apiConfig úsalo:
import { REPORTES_API } from "../api/reportesApi";

/**
 * Endpoint esperado:
 * GET /reportes/comparativo/variacion?base=2024&comparar=2025
 * Respuesta:
 * {
 *  base: 2024, comparar: 2025,
 *  resumen:{ totalBase, totalComparar, delta, porcentaje },
 *  meses:[{ mes:"01", base, comparar, diferencia, porcentaje }, ...]
 * }
 */

const MES_NOMBRE = {
  "01": "Enero",
  "02": "Febrero",
  "03": "Marzo",
  "04": "Abril",
  "05": "Mayo",
  "06": "Junio",
  "07": "Julio",
  "08": "Agosto",
  "09": "Septiembre",
  "10": "Octubre",
  "11": "Noviembre",
  "12": "Diciembre",
};

function fmtNum(n) {
  const v = Number(n || 0);
  return v.toLocaleString("es-MX");
}

function fmtPct(n) {
  const v = Number(n || 0);
  // 1 decimal si no es entero
  const fixed = Math.abs(v % 1) > 0 ? v.toFixed(1) : v.toFixed(0);
  return `${fixed}%`;
}

/**
 * Semáforo mensual:
 * - Verde: >= 0%
 * - Amarillo: caída entre -0.1% y -10%
 * - Rojo: caída < -10%
 *
 * (Puedes ajustar thresholds sin tocar lógica del resto)
 */
function semaforoMes(pct) {
  const p = Number(pct || 0);

  // Más contrastado (director friendly)
  if (p >= 0)
    return {
      label: "🟢 OK",
      dot: "bg-green-600",
      text: "text-green-800",
      bg: "bg-green-100",
      border: "border-green-300",
    };

  if (p >= -10)
    return {
      label: "🟡 ALERTA",
      dot: "bg-yellow-500",
      text: "text-yellow-900",
      bg: "bg-yellow-100",
      border: "border-yellow-300",
    };

  return {
    label: "🔴 CRÍTICO",
    dot: "bg-red-600",
    text: "text-red-800",
    bg: "bg-red-100",
    border: "border-red-300",
  };
}

/**
 * Semáforo anual (resumen):
 * - Verde: >= 0%
 * - Amarillo: caída hasta -5%
 * - Rojo: caída < -5%
 */
function semaforoAnual(pct) {
  const p = Number(pct || 0);

  // Más contrastado (director friendly)
  if (p >= 0)
    return {
      label: "🟢 Estable / Crece",
      chip: "bg-green-100 text-green-900 border-green-300",
    };
  if (p >= -5)
    return {
      label: "🟡 Leve caída",
      chip: "bg-yellow-100 text-yellow-900 border-yellow-300",
    };
  return {
    label: "🔴 Caída crítica",
    chip: "bg-red-100 text-red-900 border-red-300",
  };
}

function sign(n) {
  const v = Number(n || 0);
  if (v > 0) return `+${v}`;
  return `${v}`;
}

/* =========================
   SPARKLINE (SVG, SIN CHART.JS)
========================= */
function Sparkline({ values = [] }) {
  const w = 220;
  const h = 46;
  const pad = 4;

  if (!values.length) {
    return <div className="text-xs text-slate-500">Sin datos para tendencia</div>;
  }

  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;

  const points = values
    .map((v, i) => {
      const x = pad + (i / (values.length - 1 || 1)) * (w - pad * 2);
      const y = pad + (1 - (v - min) / range) * (h - pad * 2);
      return `${x},${y}`;
    })
    .join(" ");

  // última punta
  const last = values[values.length - 1];
  const lastX = pad + 1 * (w - pad * 2);
  const lastY = pad + (1 - (last - min) / range) * (h - pad * 2);

  return (
    <svg width={w} height={h} className="block">
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        points={points}
        className="text-indigo-600"
      />
      <circle cx={lastX} cy={lastY} r="3.5" className="fill-indigo-600" />
    </svg>
  );
}

/* =========================
   EXPORT PDF/PRINT (SIN LIBRERÍAS)
========================= */
function exportExecutivePDF(element) {
  if (!element) return;

  // Creamos una ventana nueva con el contenido y estilos mínimos
  const win = window.open("", "_blank", "width=1100,height=800");
  if (!win) return;

  const html = `
  <html>
    <head>
      <title>Reporte Ejecutivo</title>
      <meta charset="utf-8" />
      <style>
        * { font-family: Arial, Helvetica, sans-serif; }
        body { margin: 24px; background: #fff; color: #0f172a; }
        h1,h2,h3 { margin: 0; }
        .print-note { font-size: 12px; color: #64748b; margin-top: 8px; }
        /* Ocultar controles si los hubiera */
        [data-no-print="true"] { display: none !important; }
        table { width: 100%; border-collapse: collapse; margin-top: 14px; }
        th, td { border-top: 1px solid #e2e8f0; padding: 10px; text-align: left; font-size: 13px; }
        thead th { background: #f8fafc; border-top: 0; }
        .card { border: 1px solid #e2e8f0; border-radius: 14px; padding: 16px; margin-top: 14px; }
        .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        @media print {
          body { margin: 12mm; }
          .card { page-break-inside: avoid; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
        }
      </style>
    </head>
    <body>
      ${element.innerHTML}
      <div class="print-note">Generado desde el módulo KPI Ejecutivo</div>
      <script>
        window.onload = () => {
          window.print();
          setTimeout(() => window.close(), 400);
        };
      </script>
    </body>
  </html>
  `;

  win.document.open();
  win.document.write(html);
  win.document.close();
}

export default function VariacionEjecutiva() {
  const [base, setBase] = useState(2024);
  const [comparar, setComparar] = useState(2025);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  const printableRef = useRef(null);

  /* =========================
     ✅ IA (AGREGADO SIN QUITAR NADA)
  ========================= */
  const [iaLoading, setIaLoading] = useState(false);
  const [iaError, setIaError] = useState("");
  const [insightIA, setInsightIA] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setErr("");

      const url = `${REPORTES_API}/reportes/comparativo/variacion?base=${base}&comparar=${comparar}`;
      const res = await fetch(url, { method: "GET" });

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || `Error HTTP ${res.status}`);
      }

      setData(json);

      // ✅ si cambias años, limpiamos insight viejo
      setInsightIA(null);
      setIaError("");
    } catch (e) {
      setData(null);
      setErr(e.message || "Error cargando variación");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     ✅ IA: Generar Insight
     Endpoint: POST /reportes/comparativo/insight-ia
     Body: el mismo "data" que ya traes (resumen + meses)
  ========================= */
  const generarInsightIA = async () => {
    try {
      setIaLoading(true);
      setIaError("");
      setInsightIA(null);

      if (!data) {
        throw new Error("Primero consulta datos antes de generar Insight IA");
      }

      const res = await fetch(`${REPORTES_API}/reportes/comparativo/insight-ia`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tipo: "variacion_mensual",
          base,
          comparar,
          ...data,
        }),
      });

      // Tu lambda regresa JSON (body ya es JSON stringify en tu handler),
      // pero por si retorna texto JSON, lo parseamos con fallback.
      const txt = await res.text();
      let json;
      try {
        json = JSON.parse(txt);
      } catch {
        json = { resumen_ejecutivo: txt };
      }

      if (!res.ok) {
        throw new Error(json?.error || `Error IA HTTP ${res.status}`);
      }

      setInsightIA(json);
    } catch (e) {
      setIaError(e.message || "Error generando insight IA");
    } finally {
      setIaLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================
     ✅ AUTO-INSIGHT (SIN BOTÓN)
     Cuando data cambia (por primera carga o por "Consultar"),
     se dispara IA automáticamente.
  ========================= */
  useEffect(() => {
    if (!data) return;
    generarInsightIA();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const resumen = data?.resumen || null;

  const anualSem = useMemo(() => {
    return semaforoAnual(resumen?.porcentaje ?? 0);
  }, [resumen?.porcentaje]);

  const mesPico = useMemo(() => {
    if (!data?.meses?.length) return null;
    return [...data.meses].sort(
      (a, b) => Number(b.diferencia || 0) - Number(a.diferencia || 0)
    )[0];
  }, [data]);

  const mesCritico = useMemo(() => {
    if (!data?.meses?.length) return null;
    return [...data.meses].sort(
      (a, b) => Number(a.diferencia || 0) - Number(b.diferencia || 0)
    )[0];
  }, [data]);

  const insights = useMemo(() => {
    if (!data?.meses?.length || !resumen) return [];
    const meses = data.meses;

    const positivos = meses.filter((m) => Number(m.diferencia || 0) > 0).length;
    const negativos = meses.filter((m) => Number(m.diferencia || 0) < 0).length;
    const ceros = meses.length - positivos - negativos;

    const top = mesPico
      ? `${MES_NOMBRE[mesPico.mes] || mesPico.mes} (${sign(
          mesPico.diferencia
        )} / ${fmtPct(mesPico.porcentaje)})`
      : "—";
    const worst = mesCritico
      ? `${MES_NOMBRE[mesCritico.mes] || mesCritico.mes} (${sign(
          mesCritico.diferencia
        )} / ${fmtPct(mesCritico.porcentaje)})`
      : "—";

    return [
      `Total ${data.base}: ${fmtNum(resumen.totalBase)} | Total ${
        data.comparar
      }: ${fmtNum(resumen.totalComparar)}.`,
      `Balance anual: ${sign(resumen.delta)} (${fmtPct(resumen.porcentaje)}).`,
      `Mes pico: ${top}.`,
      `Mes crítico: ${worst}.`,
      `Distribución: ${positivos} meses ↑, ${negativos} meses ↓, ${ceros} meses =.`,
    ];
  }, [data, resumen, mesPico, mesCritico]);

  // Sparkline anual usando el valor "comparar" por mes
  const sparkValues = useMemo(() => {
    if (!data?.meses?.length) return [];
    return data.meses.map((m) => Number(m.comparar || 0));
  }, [data]);

  return (
    <div className="p-6 relative">
      {/* ✅ LOADING GLOBAL: cubre TODO mientras carga el reporte */}
      {loading && (
        <div className="fixed inset-0 z-50 bg-white/70 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-full border-4 border-slate-300 border-t-slate-900 animate-spin" />
            <div className="text-sm text-slate-700 font-medium">
              Procesando reporte ejecutivo…
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            KPI Ejecutivo — Variación Mensual
          </h1>
          <p className="text-slate-600 mt-1">
            Comparativo <b>{base}</b> vs <b>{comparar}</b> con semáforo automático
            y tabla ejecutiva.
          </p>
        </div>

        {/* Controles */}
        <div className="flex flex-wrap gap-2" data-no-print="true">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Base</span>
            <input
              type="number"
              value={base}
              onChange={(e) => setBase(Number(e.target.value || 0))}
              className="w-28 px-3 py-2 rounded border border-slate-200 bg-white"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Comparar</span>
            <input
              type="number"
              value={comparar}
              onChange={(e) => setComparar(Number(e.target.value || 0))}
              className="w-32 px-3 py-2 rounded border border-slate-200 bg-white"
            />
          </div>

          <button
            onClick={fetchData}
            disabled={loading}
            className="px-4 py-2 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {loading ? "Cargando..." : "Consultar"}
          </button>

          {/* Export PDF Ejecutivo (sin dependencias) */}
          <button
            onClick={() => exportExecutivePDF(printableRef.current)}
            className="px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            Exportar PDF
          </button>

          {/* ✅ BOTÓN IA ELIMINADO: ahora es automático */}
        </div>
      </div>

      {/* Error */}
      {err && (
        <div className="mt-4 p-3 rounded border border-red-200 bg-red-50 text-red-700">
          {err}
        </div>
      )}

      {/* ✅ IA Error */}
      {iaError && (
        <div className="mt-4 p-3 rounded border border-purple-200 bg-purple-50 text-purple-800">
          {iaError}
        </div>
      )}

      {/* CONTENIDO IMPRIMIBLE */}
      <div ref={printableRef}>
        {/* Cards Ejecutivo */}
        {resumen && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Card 1: Totales */}
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-sm text-slate-500">Totales</div>
              <div className="mt-2 flex items-baseline justify-between">
                <div>
                  <div className="text-xs text-slate-500">{data.base}</div>
                  <div className="text-2xl font-bold text-slate-900">
                    {fmtNum(resumen.totalBase)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">{data.comparar}</div>
                  <div className="text-2xl font-bold text-slate-900">
                    {fmtNum(resumen.totalComparar)}
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Variación */}
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-sm text-slate-500">Variación</div>
              <div className="mt-2 flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-slate-900">
                    {sign(resumen.delta)}
                  </div>
                  <div className="text-slate-600">{fmtPct(resumen.porcentaje)}</div>
                </div>
                <div
                  className={`px-3 py-1 rounded-full border text-sm font-semibold ${anualSem.chip}`}
                >
                  {anualSem.label}
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-500">
                Regla anual: Verde ≥ 0% | Amarillo hasta -5% | Rojo menor a -5%
              </div>
            </div>

            {/* Card 3: Mes pico/crítico */}
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-sm text-slate-500">Meses clave</div>
              <div className="mt-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-700">Mes pico</span>
                  <span className="font-semibold text-slate-900">
                    {mesPico ? `${MES_NOMBRE[mesPico.mes] || mesPico.mes}` : "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-slate-700">Mes crítico</span>
                  <span className="font-semibold text-slate-900">
                    {mesCritico
                      ? `${MES_NOMBRE[mesCritico.mes] || mesCritico.mes}`
                      : "—"}
                  </span>
                </div>
              </div>
            </div>

            {/* Card 4: Sparkline anual */}
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-sm text-slate-500 flex items-center justify-between">
                <span>Mini tendencia anual</span>
                <span className="text-xs text-slate-400">({data?.comparar})</span>
              </div>
              <div className="mt-3 text-indigo-600">
                <Sparkline values={sparkValues} />
              </div>
              <div className="mt-2 text-xs text-slate-500">
                Línea basada en valores mensuales del año “Comparar”.
              </div>
            </div>
          </div>
        )}

        {/* ✅ INSIGHT IA (con LOADING + DATA) */}
        {(iaLoading || insightIA) && (
          <div className="mt-10 rounded-2xl border border-violet-200 bg-[#F6F4FB] shadow-sm overflow-hidden">
            {/* HEADER */}
            <div className="px-6 py-4 border-b border-violet-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  Insight Ejecutivo IA
                </h3>
                <p className="text-xs uppercase tracking-wide text-slate-500 mt-1">
                  Análisis institucional automatizado · Bedrock / Claude
                </p>
              </div>

              {iaLoading ? (
                <span className="text-xs text-violet-700 font-semibold animate-pulse">
                  Analizando…
                </span>
              ) : (
                <div className="px-4 py-1 rounded-full bg-red-600 text-white text-xs font-bold tracking-wider shadow-inner">
                  RIESGO {insightIA?.riesgo}
                </div>
              )}
            </div>

            {/* BODY */}
            <div className="px-6 py-8 space-y-8">
              {iaLoading && (
                <>
                  <div className="h-5 bg-violet-200/60 rounded w-3/4 animate-pulse" />
                  <div className="h-4 bg-violet-200/60 rounded w-full animate-pulse" />
                  <div className="h-4 bg-violet-200/60 rounded w-5/6 animate-pulse" />
                  <div className="h-4 bg-violet-200/60 rounded w-2/3 animate-pulse" />
                </>
              )}

              {!iaLoading && insightIA && (
                <>
                  {/* IMPACTO */}
                  <div className="text-lg font-semibold text-slate-900 leading-snug max-w-3xl">
                    Impacto institucional: la contracción acumulada compromete la continuidad
                    operativa del ejercicio si la tendencia no se revierte en el corto plazo.
                  </div>

                  <hr className="border-dashed border-slate-300" />

                  {/* RESUMEN */}
                  <div>
                    <div className="uppercase text-xs tracking-wider text-slate-500 mb-3">
                      Diagnóstico ejecutivo
                    </div>
                    <p className="text-sm leading-relaxed text-slate-800 max-w-4xl">
                      {insightIA?.resumenEjecutivo}
                    </p>
                  </div>

                  {/* HALLAZGOS */}
                  <div>
                    <div className="uppercase text-xs tracking-wider text-slate-500 mb-4">
                      Evidencia relevante
                    </div>
                    <ul className="space-y-3 text-sm text-slate-800">
                      {(insightIA?.hallazgosClave || []).map((h, idx) => (
                        <li key={idx} className="flex gap-3 border-l-2 border-red-500 pl-3">
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* RECOMENDACIÓN */}
                  <div className="border-l-4 border-violet-700 bg-white rounded-r-lg p-5">
                    <div className="uppercase text-xs tracking-wider text-slate-500 mb-2">
                      Línea de decisión ejecutiva
                    </div>
                    <p className="text-sm leading-relaxed text-slate-800 max-w-4xl">
                      {insightIA?.recomendacionEjecutiva}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Tabla tipo "Secretaría" */}
        {data?.meses?.length > 0 && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-lg font-semibold text-slate-900">
                  Detalle mensual
                </div>
                <div className="text-sm text-slate-500">
                  {data.base} vs {data.comparar} — Diferencia y Caída (%)
                </div>
              </div>
              <div className="text-xs text-slate-500">
                Semáforo mensual: Verde ≥ 0% | Amarillo (0 a -10%) | Rojo &lt; -10%
              </div>
            </div>

            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-600">
                    <th className="px-4 py-3 font-semibold">Mes</th>
                    <th className="px-4 py-3 font-semibold">{data.base}</th>
                    <th className="px-4 py-3 font-semibold">{data.comparar}</th>
                    <th className="px-4 py-3 font-semibold">Diferencia</th>
                    <th className="px-4 py-3 font-semibold">Caída (%)</th>
                    <th className="px-4 py-3 font-semibold">Semáforo</th>
                  </tr>
                </thead>

                <tbody>
                  {data.meses.map((m) => {
                    const diff = Number(m.diferencia || 0);
                    const pct = Number(m.porcentaje || 0);
                    const sem = semaforoMes(pct);

                    const diffColor =
                      diff > 0
                        ? "text-green-800"
                        : diff < 0
                        ? "text-red-800"
                        : "text-slate-500";

                    const pctColor =
                      pct > 0
                        ? "text-green-800"
                        : pct < 0
                        ? "text-red-800"
                        : "text-slate-500";

                    return (
                      <tr key={m.mes} className="border-t border-slate-100">
                        <td className="px-4 py-3 font-medium text-slate-900">
                          {MES_NOMBRE[m.mes] || m.mes}
                        </td>

                        <td className="px-4 py-3 text-slate-900">
                          {fmtNum(m.base)}
                        </td>
                        <td className="px-4 py-3 text-slate-900">
                          {fmtNum(m.comparar)}
                        </td>

                        <td className={`px-4 py-3 font-semibold ${diffColor}`}>
                          {diff > 0 ? `+${fmtNum(diff)}` : fmtNum(diff)}
                        </td>

                        <td className={`px-4 py-3 font-semibold ${pctColor}`}>
                          {pct > 0 ? `+${fmtPct(pct)}` : fmtPct(pct)}
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${sem.bg} ${sem.border}`}
                          >
                            <span className={`w-2.5 h-2.5 rounded-full ${sem.dot}`} />
                            <span className={`text-xs font-bold ${sem.text}`}>
                              {sem.label}
                            </span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Insights */}
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <div className="text-sm font-semibold text-slate-900">
                Insight ejecutivo
              </div>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {insights.map((t, idx) => (
                  <li key={idx}>• {t}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && !err && (!data?.meses || data.meses.length === 0) && (
          <div className="mt-6 p-6 rounded-xl border border-slate-200 bg-white text-slate-600">
            No hay datos para mostrar todavía.
          </div>
        )}
      </div>
    </div>
  );
}
